import { TooltipProvider } from "@/components/ui/tooltip";
import { Bold, Strikethrough } from "lucide-react";
import { useCallback, useRef } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarFontSizeMenu,
  CanvasToolbarTypefaceMenu,
} from "../components/toolbar";
import { LABEL_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import type { ResolvedLineLabelStyle } from "./shared/LineLabel";

interface LineLabelFloatingToolbarProps {
  style: ResolvedLineLabelStyle;
  onStyleChange: (patch: Partial<ResolvedLineLabelStyle>) => void;
  onPopoverOpenChange?: (open: boolean) => void;
}

/**
 * The bar shown while a line's label is being edited: Colour · Typeface ·
 * Size · Bold · Strikethrough. The text bar minus what a one-line label has
 * no use for (align, lists, emoji).
 */
export function LineLabelFloatingToolbar({
  style,
  onStyleChange,
  onPopoverOpenChange,
}: LineLabelFloatingToolbarProps) {
  const isColorOpenRef = useRef(false);
  const isTypefaceOpenRef = useRef(false);
  const isSizeOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onPopoverOpenChange?.(
      isColorOpenRef.current ||
        isTypefaceOpenRef.current ||
        isSizeOpenRef.current,
    );
  }, [onPopoverOpenChange]);

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );
  const handleTypefaceOpen = useCallback(
    (open: boolean) => {
      isTypefaceOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );
  const handleSizeOpen = useCallback(
    (open: boolean) => {
      isSizeOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest("input")) return;
          e.preventDefault();
        }}
      >
        <CanvasColorPickerButton
          colors={LABEL_COLORS}
          role="text"
          activeValue={style.text_color}
          tooltip="Text color"
          onColorChange={(color) => onStyleChange({ text_color: color })}
          onOpenChange={handleColorOpen}
        />

        <CanvasToolbarTypefaceMenu
          fontFamily={style.font_family}
          onFontFamilyChange={(font_family) => onStyleChange({ font_family })}
          onOpenChange={handleTypefaceOpen}
        />

        <CanvasToolbarFontSizeMenu
          fontSize={style.font_size}
          onFontSizeChange={(font_size) => onStyleChange({ font_size })}
          onOpenChange={handleSizeOpen}
        />

        <CanvasToolbarDivider />

        <CanvasToolbarButton
          label="Bold"
          active={style.is_bold}
          onClick={(e) => {
            e.stopPropagation();
            onStyleChange({ is_bold: !style.is_bold });
          }}
        >
          <Bold className="size-4" />
        </CanvasToolbarButton>
        <CanvasToolbarButton
          label="Strikethrough"
          active={style.is_strikethrough}
          onClick={(e) => {
            e.stopPropagation();
            onStyleChange({ is_strikethrough: !style.is_strikethrough });
          }}
        >
          <Strikethrough className="size-4" />
        </CanvasToolbarButton>
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
